import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'
import { useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import type { ButtonProps } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast-v2'

import { isDateStarted } from 'app/KinerjaShared/utils/pelaksanaan'
import { assetUrl } from 'utils/url'
import { PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { useAPIKinerjaProfile } from 'api/kinerja/useAPIKinerja'
import { useUpsertPerencanaan } from 'app/KinerjaGpkPage/hooks/use-upsert-perencanaan'
import {
  useAPIUpsertPelaksanaanDocumentPreparation,
  API_PATH_PELAKSANAAN_PROGRESS,
} from 'api/kinerja/useAPIKinerja'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { KinerjaStatusGpk } from 'types/kinerja-gpk'

export type PerencanaanPegawaiCardProps = {
  revision?: string
  state: KinerjaStatusGpk
}

const STATE_CONTENT: {
  [key in KinerjaStatusGpk]?: {
    title: string
    description: string
    ctaText: string
    ctaColor: ButtonProps['color']
    imgPath: string
    href: string
  }
} = {
  NOT_FOUND: {
    title: 'Mulai Perencanaan Kinerja',
    description:
      'Isi Pelaksanaan Tugas Pokok, Praktik Kinerja, Pengembangan Kompetensi, dan Perilaku Kerja. Diskusikan juga dengan Tim Kinerja dan Pejabat Penilai Kinerja.',
    ctaText: 'Mulai',
    ctaColor: 'blue',
    imgPath: '/illustrations/img-to-do-list-document.png',
    href: `${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/perencanaan`,
  },
  DRAFT: {
    title: 'Lanjutkan Perencanaan Kinerja',
    description:
      'Isi Pelaksanaan Tugas Pokok, Praktik Kinerja, Pengembangan Kompetensi, dan Perilaku Kerja. Diskusikan juga dengan Tim Kinerja dan Pejabat Penilai Kinerja.',
    ctaText: 'Lanjutkan',
    ctaColor: 'blue',
    imgPath: '/illustrations/img-to-do-list-document.png',
    href: `${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/perencanaan`,
  },
  WAITING_APPROVAL: {
    title: 'Menunggu pemeriksaan',
    description:
      'Perencanaan Kinerja Anda sudah diajukan. Anda dapat konfirmasi ke Tim Kinerja dan Pejabat Penilai Kinerja untuk melakukan pemeriksaan.',
    ctaText: 'Cek Perencanaan Kinerja',
    ctaColor: 'white',
    imgPath: '/illustrations/img-time-document.png',
    href: `${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/perencanaan`,
  },
  WAITING_APPROVAL_PPK: {
    title: 'Menunggu pemeriksaan',
    description:
      'Perencanaan Kinerja Anda sudah diajukan. Anda dapat konfirmasi ke Tim Kinerja dan Pejabat Penilai Kinerja untuk melakukan pemeriksaan.',
    ctaText: 'Cek Perencanaan Kinerja',
    ctaColor: 'white',
    imgPath: '/illustrations/img-time-document.png',
    href: `${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/perencanaan`,
  },
  REVISION_TK: {
    title: 'Lakukan revisi sesuai arahan Tim Kinerja',
    description: 'Lakukan revisi dan ajukan ulang Perencanaan Anda. ',
    ctaText: 'Lakukan revisi',
    ctaColor: 'blue',
    imgPath: '/illustrations/img-folder-document-rejected.png',
    href: `${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/perencanaan`,
  },
  REVISION_PPK: {
    title: 'Lakukan revisi sesuai arahan Pejabat Penilai Kinerja',
    description: 'Lakukan revisi dan ajukan ulang Perencanaan Anda. ',
    ctaText: 'Lakukan revisi',
    ctaColor: 'blue',
    imgPath: '/illustrations/img-folder-document-rejected.png',
    href: `${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/perencanaan`,
  },
  APPROVED: {
    title: 'Disetujui Pejabat Penilai Kinerja',
    description:
      'Perencanaan Anda sudah disetujui dan otomatis dikirim ke E-Kinerja dalam 1x24 jam. Anda dapat lanjut mulai ke tahap berikutnya: Pelaksanaan.',
    ctaText: 'Mulai Pelaksanaan',
    ctaColor: 'blue',
    imgPath: '/illustrations/img-handshake.png',
    href: '',
  },
  READY: {
    title: 'Disetujui Pejabat Penilai Kinerja',
    description:
      'Perencanaan Anda sudah disetujui dan otomatis dikirim ke E-Kinerja dalam 1x24 jam. Anda dapat lanjut mulai ke tahap berikutnya: Pelaksanaan.',
    ctaText: 'Mulai Pelaksanaan',
    ctaColor: 'blue',
    imgPath: '/illustrations/img-handshake.png',
    href: '',
  },
}

export function PerencanaanPegawaiCard({
  revision,
  state,
}: PerencanaanPegawaiCardProps) {
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data: profileData } = useAPIKinerjaProfile({
    options: {
      staleTime: 1 * 24 * 60 * 60 * 1000, // 1 day
    },
  })

  // -----------------------------------------
  // Get pelaksanaan starting date
  // -----------------------------------------
  const { period } = usePeriodContext()

  const isPelaksanaanDateStarted = useMemo(() => {
    return isDateStarted(period.periodeKinerja)
  }, [period.periodeKinerja])

  // -----------------------------------------
  // Handle create perencanaan for NOT_FOUND state
  // -----------------------------------------
  const { handleUpsertMutation, isLoading: isLoadingUpsertPerencanaan } =
    useUpsertPerencanaan()

  const handleInitPerencanaan = () => {
    handleUpsertMutation(
      {
        tipe: 'GPK',
        pangkatGolongan: profileData?.data?.pangkatGolongan,
        jenjang: profileData?.data?.jenjang,
      },
      {
        successMessage: 'Berhasil memulai perencanaan',
        onSuccess: () => {
          router.push(`${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/perencanaan`)
        },
        errorMessage: 'Gagal memulai perencanaan',
      }
    )
  }

  // -----------------------------------------
  // Handle create pelaksanaan draft for
  // APPROVED and READY states
  // -----------------------------------------
  const documentPreparationMutation =
    useAPIUpsertPelaksanaanDocumentPreparation()

  const handleInitPelaksanaan = () => {
    if (documentPreparationMutation.isLoading) return

    // To init pelaksanaan, we need to create a draft of document preparation
    documentPreparationMutation.mutate(
      {
        tipe: 'GPK',
        state: 'DRAFT',
        detail: {
          behavioralTargets: [],
          behavioralTargetEffort: '',
          observationDate: '',
          observationLevel: '',
          observationClass: '',
          observationSubject: '',
          observationNote: '',
          observationDocuments: [],
          observationLocation: { npsn: '' },
          observationTool: '',
        },
      },
      {
        onSuccess: () => {
          toast({ message: 'Berhasil memulai pelaksanaan' })

          // Refetch pelaksanaan progress data
          queryClient.invalidateQueries([API_PATH_PELAKSANAAN_PROGRESS])
        },
        onError: () => {
          toast({ message: 'Gagal memulai pelaksanaan. Coba lagi.' })
        },
      }
    )
  }

  // -----------------------------------------
  // Resolve contents
  // -----------------------------------------
  const contents = STATE_CONTENT[state]

  const { title, description, ctaText, ctaColor, imgPath, href } = contents

  const showRevision =
    revision && (state === 'REVISION_TK' || state === 'REVISION_PPK')

  return (
    <section className="shadow-card rounded-lg p-6 flex items-center justify-between gap-6 bg-white">
      <div className="flex items-center gap-6">
        <img
          alt=""
          src={assetUrl(imgPath)}
          width={84}
          height={84}
          className="flex-shrink-0"
        />
        <div>
          <Text variant="heading-lg" as="h2" className="mb-1">
            {title}
          </Text>
          <Text color="subdued">{description}</Text>
          {showRevision && (
            <div className="bg-surface-warning-subdued border border-warning px-4 py-2 rounded-md w-full overflow-wrap-anywhere mt-3">
              <Text variant="body-sm">Alasan Revisi: {revision}</Text>
            </div>
          )}
        </div>
      </div>
      {state === 'NOT_FOUND' ? (
        <Button
          size="lg"
          color={ctaColor}
          className="min-w-60 whitespace-nowrap"
          disabled={!ctaText || isLoadingUpsertPerencanaan}
          isLoading={isLoadingUpsertPerencanaan}
          onClick={handleInitPerencanaan}
        >
          {ctaText}
        </Button>
      ) : state === 'APPROVED' || state === 'READY' ? (
        <Button
          size="lg"
          color={ctaColor}
          className="min-w-60 whitespace-nowrap"
          disabled={
            !ctaText || isLoadingUpsertPerencanaan || !isPelaksanaanDateStarted
          }
          isLoading={documentPreparationMutation.isLoading}
          onClick={handleInitPelaksanaan}
        >
          {ctaText}
        </Button>
      ) : (
        <NextLink href={href} passHref>
          <Button
            size="lg"
            as="a"
            type={undefined}
            color={ctaColor}
            className="min-w-60 whitespace-nowrap"
            disabled={!ctaText}
          >
            {ctaText}
          </Button>
        </NextLink>
      )}
    </section>
  )
}
